/**
 * Copyright 2022 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useUpdateCustomerMutation } from "~/generated/graphql";
import { showError, showSuccess } from "~/components";
import { CustomerFormSchemaType } from "~/views/customers/components/Form/schema";

type UpdateCustomerProps = {
  onSuccess: () => void;
  onError: () => void;
};

export function useUpdateCustomer({ onSuccess, onError }: UpdateCustomerProps) {
  const [updateCustomerMutation, { loading: isLoading }] =
    useUpdateCustomerMutation();

  function updateCustomer(id: string, data: CustomerFormSchemaType) {
    return updateCustomerMutation({
      variables: {
        input: {
          customerId: id,
          name: data.name,
          backupPoliciesBoundary: {
            minRPOInHours: data.rpo,
            maxPrimarySnapshotRetentionQuantity:
              data.primarySnapshotsRetentionQuantity,
            maxPrimarySnapshotRetentionInDays:
              data.primarySnapshotsRetentionInDays,
            maxReplicaSnapshotRetentionQuantity:
              data.replicaSnapshotsRetentionQuantity,
            maxReplicaSnapshotRetentionInDays:
              data.replicaSnapshotsRetentionInDays,
          },
        },
      },
    })
      .then(() => {
        showSuccess("Customer has been updated.");
        onSuccess();
        return null;
      })
      .catch(() => {
        showError("Something went wrong.");
        onError();
      });
  }

  return [updateCustomer, isLoading] as const;
}
